import { useEffect, useState } from "react";
import { CandidateStatus, RoleStatus } from "../../../Models";
import { CandidateAction } from "./RoleCandidates";
import useApiToken from "../../../hooks/useApiToken";
import Notes from "./Notes";
import { PencilSquareIcon } from "@heroicons/react/20/solid";
import { gradeColors } from "../../../helpers/constants";
import { SubmitManualEvaluation } from "../../../api/candidates.api";

const getRatingGrade = (rating: string | undefined) => {
  if (!rating) return "";
  return rating === "Excellent"
    ? "E"
    : rating === "Pass"
      ? "P"
      : rating === "Fail"
        ? "F"
        : rating === "Waitlist"
          ? "W"
          : "";
};

export default function Grade({
  roleStatuses,
  candidate,
  handleUpdateCandidates,
}: {
  roleStatuses: RoleStatus[];
  candidate: CandidateAction;
  handleUpdateCandidates: (
    candidatesUpdate: CandidateStatus[],
    gradeStatusUpdate?: boolean,
    manualEvaluation?: { roleStatusId: string; rating: string; score?: number }
  ) => void;
}) {
  const [grades, setGrades] = useState<string[]>([]);
  const [currentStatus, setCurrentStatus] = useState<RoleStatus>();
  const [noteIsOpen, setNoteIsOpen] = useState(false);
  const [gradeIsOpen, setGradeIsOpen] = useState(false);
  const token = useApiToken();

  useEffect(() => {
    const candidateGrades = roleStatuses.slice(1, -2).map((rs) => {
      if (
        rs.roleStatusType?.name === "Interview" ||
        rs.roleStatusType?.name === "Assignment"
      ) {
        const manualEval = candidate.manualEvaluations?.find(
          (e) => e.roleStatusId === rs.id
        );
        const grade = getRatingGrade(manualEval?.rating);
        return grade;
      }
      return "";
    });

    setGrades(candidateGrades);
  }, [roleStatuses, candidate]);

  useEffect(() => {
    const handleScroll = () => {
      if (gradeIsOpen) {
        setGradeIsOpen(false);
        setNoteIsOpen(false);
      }
    };

    if (gradeIsOpen) {
      window.addEventListener("scroll", handleScroll);
    } else {
      window.removeEventListener("scroll", handleScroll);
    }
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [gradeIsOpen, setNoteIsOpen]);

  const handleGradeClick = (
    index: number,
    clickedGrade: string,
    rs: RoleStatus
  ) => {
    const candidateStatus = candidate.candidateStatuses.find(
      (c) => c.roleStatusId === rs.id
    );
    if (!token || !candidateStatus) return;

    const currentGrade = grades[index];
    const isTogglingOff = currentGrade === clickedGrade;

    // For Interview/Assignment types, use SubmitManualEvaluation
    if (
      rs.roleStatusType?.name === "Interview" ||
      rs.roleStatusType?.name === "Assignment"
    ) {
      const rating = isTogglingOff
        ? ""
        : clickedGrade === "E"
          ? "Excellent"
          : clickedGrade === "P"
            ? "Pass"
            : clickedGrade === "F"
              ? "Fail"
              : clickedGrade === "W"
                ? "Waitlist"
                : "";

      SubmitManualEvaluation(
        {
          candidateId: candidate.id,
          roleId: candidateStatus.roleId,
          roleStatusId: rs.id,
          rating,
        },
        token
      )
        .then(() => {
          const newGrades = [...grades];
          newGrades[index] = isTogglingOff ? "" : clickedGrade;
          setGrades(newGrades);

          handleUpdateCandidates([candidateStatus], true, {
            roleStatusId: rs.id,
            rating,
          });
        })
        .catch((error) => {
          console.error("Failed to update manual evaluation:", error);
        });
    }
  };

  const candidateReachedStatus = (rs: RoleStatus) => {
    return candidate.candidateStatuses.some((c) => c.roleStatusId === rs.id);
  };

  return (
    <div
      className="relative cursor-default"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className="">
        <div className="relative">
          <button
            className="flex w-full items-center justify-between text-sm text-gray-400 hover:text-gray-500"
            onClick={() => {
              setGradeIsOpen(true);
            }}
          >
            <div className="flex space-x-1">
              {roleStatuses.slice(1, -2).map((rs, index) => {
                // Only show grades for Interview/Assignment types
                const isManualGradingType =
                  rs.roleStatusType?.name === "Interview" ||
                  rs.roleStatusType?.name === "Assignment";
                if (!isManualGradingType) return null;
                var grade = grades[index] as "E" | "P" | "F" | "W";
                return (
                  <span
                    key={rs.id}
                    title={rs.name}
                    className={`px-2 py-1 rounded-lg text-xs font-semibold transition-all duration-200 border-2 ${
                      gradeColors[grade]?.bg || "bg-gray-200 dark:bg-gray-700"
                    } ${gradeColors[grade]?.text || "text-gray-500"} ${gradeColors[grade]?.hover || ""}`}
                  >
                    {grades[index] || "-"}
                  </span>
                );
              })}
            </div>
          </button>
          {gradeIsOpen && (
            <div
              className={
                "py-3 px-4 absolute -right-4 bg-white rounded-md z-[11] border border-gray-200 shadow-xl h-fit dark:bg-darkbglight dark:border-gray-500"
              }
            >
              <div className="flex justify-between w-full">
                <div className={`space-y-3`}>
                  {grades &&
                    roleStatuses.slice(1, -2).map((rs, index) => {
                      // Only show manual grading for Interview/Assignment types
                      const isManualGradingType =
                        rs.roleStatusType?.name === "Interview" ||
                        rs.roleStatusType?.name === "Assignment";
                      if (!isManualGradingType) return null;

                      return (
                        <div
                          key={index}
                          className={`flex relative ${
                            !candidateReachedStatus(rs) ? "hidden" : "block"
                          }`}
                        >
                          <div className="flex items-center font-medium text-gray-700 min-w-[9rem] dark:text-gray-300">
                            <PencilSquareIcon
                              height={16}
                              className="pr-1 font-normal cursor-pointer"
                              onClick={() => {
                                setCurrentStatus(rs);
                                if (rs.id === currentStatus?.id)
                                  setNoteIsOpen(!noteIsOpen);
                                else setNoteIsOpen(true);
                              }}
                            />
                            {rs.name} :{" "}
                          </div>
                          <div className="flex space-x-1">
                            {(["E", "P", "F", "W"] as const).map(
                              (gradeOption) => (
                                <span
                                  key={gradeOption}
                                  onClick={() =>
                                    handleGradeClick(index, gradeOption, rs)
                                  }
                                  className={`
                              px-2 py-1 rounded-lg text-xs font-semibold transition-all duration-200 border-2
                              ${
                                grades[index] === gradeOption
                                  ? `${gradeColors[gradeOption].bg} ${gradeColors[gradeOption].text} shadow-md ring-2 ${gradeColors[gradeOption].ring} scale-105`
                                  : `bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${gradeColors[gradeOption].inactive} hover:scale-105 hover:shadow-md`
                              }
                            `}
                                  // className={`${
                                  //   grades[index] === gradeOption
                                  //     ? gradeOption === "E"
                                  //       ? "bg-green-600 text-white"
                                  //       : gradeOption === "P"
                                  //       ? "bg-blue-600 text-white"
                                  //       : gradeOption === "F"
                                  //       ? "bg-red-600 text-white"
                                  //       : "bg-yellow-600 text-white"
                                  //     : ""
                                  // } rounded-full py-1 px-2 text-xs font-medium cursor-pointer`}
                                >
                                  {gradeOption === "E"
                                    ? "Excellent"
                                    : gradeOption === "P"
                                      ? "Pass"
                                      : gradeOption === "F"
                                        ? "Fail"
                                        : "Waitlist"}
                                </span>
                              )
                            )}
                          </div>
                          {noteIsOpen && currentStatus?.id === rs.id && (
                            <Notes
                              candidateId={candidate.id}
                              roleStatus={currentStatus}
                            />
                          )}
                        </div>
                      );
                    })}
                </div>
                <div></div>
              </div>
            </div>
          )}
          {gradeIsOpen && (
            <div
              className="fixed inset-0 z-10 h-full"
              onClick={() => {
                setGradeIsOpen(false);
                setNoteIsOpen(false);
              }}
            ></div>
          )}
        </div>
      </div>
    </div>
  );
}
